import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import { useOnboardingStore } from '../../store/useOnboardingStore';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalysisStore } from '../../store/useAnalysisStore';

import { createHomeTourSteps } from './steps/homeTourSteps';
import { createPreparationTourSteps } from './steps/preparationTourSteps';
import { createResultsTourSteps } from './steps/resultsTourSteps';
import { createSpeechTourSteps } from './steps/speechTourSteps';
import { createPracticeTourSteps } from './steps/practiceTourSteps';
import { createIdeasTourSteps } from './steps/ideasTourSteps';

import './onboarding.css';

const OnboardingTour: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { step: analysisStep } = useAnalysisStore();

  const { phase, completed, skipped, setPhase, complete, skip, syncToBackend } =
    useOnboardingStore();

  const driverRef = useRef<Driver | null>(null);
  const isRunningRef = useRef<boolean>(false);

  const destroyTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
      isRunningRef.current = false;
    }
  }, []);

  const handleSkip = useCallback(() => {
    skip();
    syncToBackend();
    destroyTour();
  }, [skip, syncToBackend, destroyTour]);

  const runHomeTour = useCallback(() => {
    if (isRunningRef.current) return;

    destroyTour();

    const steps = createHomeTourSteps(t, navigate, () => {
      setPhase('preparation');
    });

    const driverObj = driver({
      steps,
      showProgress: true,
      allowClose: true,
      overlayClickBehavior: 'close',
      onDestroyed: () => {
        isRunningRef.current = false;
      },
      onCloseClick: handleSkip,
    });

    driverRef.current = driverObj;
    isRunningRef.current = true;
    driverObj.drive();
  }, [t, navigate, setPhase, handleSkip, destroyTour]);

  const runPreparationTour = useCallback(
    (currentWizardStep?: number) => {
      if (isRunningRef.current) return;

      destroyTour();

      const steps = createPreparationTourSteps(t, currentWizardStep ?? analysisStep);

      if (steps.length === 0) return;

      const driverObj = driver({
        steps,
        showProgress: true,
        allowClose: true,
        overlayClickBehavior: 'close',
        onDestroyed: () => {
          isRunningRef.current = false;
        },
        onCloseClick: handleSkip,
      });

      driverRef.current = driverObj;
      isRunningRef.current = true;
      driverObj.drive();
    },
    [t, analysisStep, handleSkip, destroyTour],
  );

  const runResultsTour = useCallback(() => {
    if (isRunningRef.current) return;

    destroyTour();

    const steps = createResultsTourSteps(t);

    const driverObj = driver({
      steps,
      showProgress: true,
      allowClose: true,
      overlayClickBehavior: 'close',
      onDestroyed: () => {
        isRunningRef.current = false;
      },
      onNextClick: () => {
        if (driverObj.isLastStep()) {
          setPhase('speech');
          destroyTour();
        } else {
          driverObj.moveNext();
        }
      },
      onCloseClick: handleSkip,
    });

    driverRef.current = driverObj;
    isRunningRef.current = true;
    driverObj.drive();
  }, [t, setPhase, handleSkip, destroyTour]);

  const runSpeechTour = useCallback(() => {
    if (isRunningRef.current) return;

    destroyTour();

    const steps = createSpeechTourSteps(t);

    const driverObj = driver({
      steps,
      showProgress: true,
      allowClose: true,
      overlayClickBehavior: 'close',
      onDestroyed: () => {
        isRunningRef.current = false;
      },
      onNextClick: () => {
        if (driverObj.isLastStep()) {
          setPhase('practice');
          destroyTour();
        } else {
          driverObj.moveNext();
        }
      },
      onCloseClick: handleSkip,
    });

    driverRef.current = driverObj;
    isRunningRef.current = true;
    driverObj.drive();
  }, [t, setPhase, handleSkip, destroyTour]);

  const runPracticeTour = useCallback(() => {
    if (isRunningRef.current) return;

    destroyTour();

    const steps = createPracticeTourSteps(t);

    const driverObj = driver({
      steps,
      showProgress: true,
      allowClose: true,
      overlayClickBehavior: 'close',
      onDestroyed: () => {
        isRunningRef.current = false;
      },
      onNextClick: () => {
        if (driverObj.isLastStep()) {
          complete();
          syncToBackend();
          destroyTour();
        } else {
          driverObj.moveNext();
        }
      },
      onCloseClick: handleSkip,
    });

    driverRef.current = driverObj;
    isRunningRef.current = true;
    driverObj.drive();
  }, [t, complete, handleSkip, syncToBackend, destroyTour]);

  const runIdeasTour = useCallback(() => {
    if (isRunningRef.current) return;

    destroyTour();

    const steps = createIdeasTourSteps(t);

    const driverObj = driver({
      steps,
      showProgress: true,
      allowClose: true,
      overlayClickBehavior: 'close',
      onDestroyed: () => {
        isRunningRef.current = false;
      },
      onCloseClick: handleSkip,
    });

    driverRef.current = driverObj;
    isRunningRef.current = true;
    driverObj.drive();
  }, [t, handleSkip, destroyTour]);

  // Main effect: handle route changes and phase transitions
  useEffect(() => {
    // Don't run if completed, skipped, or no user
    if (completed || skipped || !user) return;

    // Don't run if user hasn't completed the survey (onboarding questionnaire)
    const extendedUser = user as Record<string, any>;
    if (extendedUser.survey_completed === false) return;

    const path = location.pathname;

    // Phase: idle on /home — start home tour
    if (phase === 'idle' && path === '/home') {
      setPhase('home');
      setTimeout(() => runHomeTour(), 500);
      return;
    }

    // Phase transitions based on route
    if (path === '/preparation' && phase === 'home') {
      setPhase('preparation');
    }

    if (path === '/preparation' && phase === 'preparation') {
      // Run preparation tour for current wizard step
      setTimeout(() => runPreparationTour(), 500);
    }

    // Ideas generator tour
    if (path === '/idea-generator' && (phase === 'idle' || phase === 'home')) {
      setPhase('ideas');
      setTimeout(() => runIdeasTour(), 500);
    }

    // Detect results page after preparation phase
    if (path.startsWith('/results/') && (phase === 'preparation' || phase === 'idle')) {
      setPhase('results');
      setTimeout(() => runResultsTour(), 500);
    }

    // Detect speech generation page
    if (path.startsWith('/generated-speech/') && phase === 'results') {
      setPhase('speech');
      setTimeout(() => runSpeechTour(), 500);
    }

    // Detect practice page
    if (path.startsWith('/practice/') && phase === 'speech') {
      setPhase('practice');
      setTimeout(() => runPracticeTour(), 500);
    }

    // Cleanup on unmount
    return () => {
      destroyTour();
    };
  }, [
    location.pathname,
    user,
    phase,
    completed,
    skipped,
    analysisStep,
    runHomeTour,
    runPracticeTour,
    runPreparationTour,
    runResultsTour,
    runSpeechTour,
    runIdeasTour,
    setPhase,
    destroyTour,
  ]);

  // Listen for wizard step changes during preparation phase
  useEffect(() => {
    if (phase === 'preparation' && location.pathname === '/preparation') {
      runPreparationTour(analysisStep);
    }
  }, [analysisStep, location.pathname, phase, runPreparationTour]);

  // Listen for ideas generator route
  useEffect(() => {
    if (phase === 'ideas' && location.pathname === '/idea-generator') {
      runIdeasTour();
    }
  }, [location.pathname, phase, runIdeasTour]);

  // Destroy tour on completion or skip
  useEffect(() => {
    if (completed || skipped) {
      destroyTour();
    }
  }, [completed, skipped, destroyTour]);

  return null;
};

export default OnboardingTour;
